import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { patientQueryKeys, patientsApi } from "../../../shared/api/patients";
import type {
  AllergyCategory,
  AllergyCreateRequest,
  AllergyResponse,
  AllergySeverity,
} from "../../../shared/types/patient";
import {
  allergyCategoryLabel,
  allergySeverityLabel,
  formatDate,
} from "../../../shared/format";
import { apiError } from "../../../shared/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const severityBadgeVariant: Record<AllergySeverity, BadgeProps["variant"]> = {
  MILD: "muted",
  MODERATE: "warning",
  SEVERE: "destructive",
  ANAPHYLAXIS: "destructive",
};

export function AllergiesPanel({ patientId }: { patientId: string }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const list = useQuery({
    queryKey: patientQueryKeys.allergies(patientId, false),
    queryFn: () => patientsApi.listAllergies(patientId, false),
  });

  const deactivate = useMutation({
    mutationFn: (allergyId: string) => patientsApi.deleteAllergy(patientId, allergyId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: patientQueryKeys.allergies(patientId, false) }),
  });

  return (
    <Card>
      <CardContent className="space-y-4 p-4 sm:p-6">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold text-foreground">Dị ứng</h2>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setAdding((v) => !v)}
          >
            {adding ? (
              "Đóng"
            ) : (
              <>
                <Plus className="h-4 w-4" />
                Thêm dị ứng
              </>
            )}
          </Button>
        </div>

        {adding && (
          <AddAllergyForm
            patientId={patientId}
            onDone={() => {
              setAdding(false);
              qc.invalidateQueries({
                queryKey: patientQueryKeys.allergies(patientId, false),
              });
            }}
          />
        )}

        {list.isLoading && <p className="text-sm text-muted-foreground">Đang tải…</p>}
        {list.isError && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {apiError(list.error).detail}
          </div>
        )}
        {list.data && list.data.length === 0 && !adding && (
          <p className="text-sm text-muted-foreground">
            Chưa có thông tin dị ứng. Hãy hỏi bệnh nhân trước khi kê đơn.
          </p>
        )}

        <div className="divide-y divide-border">
          {list.data?.map((a) => (
            <AllergyRow
              key={a.id}
              allergy={a}
              onDelete={() => deactivate.mutate(a.id)}
              busy={deactivate.isPending}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function AllergyRow({
  allergy,
  onDelete,
  busy,
}: {
  allergy: AllergyResponse;
  onDelete: () => void;
  busy: boolean;
}) {
  return (
    <div className="flex flex-col gap-3 py-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
          <span className="truncate">{allergy.substance}</span>
          <Badge variant={severityBadgeVariant[allergy.severity]}>
            {allergySeverityLabel[allergy.severity]}
          </Badge>
        </div>
        <div className="text-sm text-muted-foreground">
          {allergyCategoryLabel[allergy.category]}
          {allergy.reaction && ` · ${allergy.reaction}`}
          {allergy.onsetDate && ` · Khởi phát ${formatDate(allergy.onsetDate)}`}
        </div>
      </div>
      <div className="shrink-0">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onDelete}
          disabled={busy}
        >
          Vô hiệu
        </Button>
      </div>
    </div>
  );
}

function AddAllergyForm({ patientId, onDone }: { patientId: string; onDone: () => void }) {
  const [data, setData] = useState<AllergyCreateRequest>({
    category: "DRUG",
    substance: "",
    severity: "MODERATE",
    reaction: "",
    onsetDate: null,
    source: "PATIENT_REPORTED",
  });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (req: AllergyCreateRequest) => patientsApi.createAllergy(patientId, req),
    onSuccess: () => onDone(),
    onError: (e) => setError(apiError(e).detail),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!data.substance.trim()) {
          setError("Vui lòng nhập tên dị ứng");
          return;
        }
        setError(null);
        create.mutate(data);
      }}
      className="rounded-md border bg-muted/40 p-4"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="al-cat">Phân loại</Label>
          <Select
            value={data.category}
            onValueChange={(v) =>
              setData((d) => ({ ...d, category: v as AllergyCategory }))
            }
          >
            <SelectTrigger id="al-cat">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(allergyCategoryLabel) as AllergyCategory[]).map((c) => (
                <SelectItem key={c} value={c}>
                  {allergyCategoryLabel[c]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="al-sub">Chất dị ứng *</Label>
          <Input
            id="al-sub"
            type="text"
            value={data.substance}
            onChange={(e) => setData((d) => ({ ...d, substance: e.target.value }))}
            placeholder="VD: Penicillin, hải sản…"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="al-sev">Mức độ</Label>
          <Select
            value={data.severity}
            onValueChange={(v) =>
              setData((d) => ({ ...d, severity: v as AllergySeverity }))
            }
          >
            <SelectTrigger id="al-sev">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(allergySeverityLabel) as AllergySeverity[]).map((s) => (
                <SelectItem key={s} value={s}>
                  {allergySeverityLabel[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="al-react">Phản ứng</Label>
          <Input
            id="al-react"
            type="text"
            value={data.reaction ?? ""}
            onChange={(e) => setData((d) => ({ ...d, reaction: e.target.value || null }))}
            placeholder="VD: nổi mề đay, khó thở"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="al-onset">Khởi phát</Label>
          <Input
            id="al-onset"
            type="date"
            value={data.onsetDate ?? ""}
            onChange={(e) =>
              setData((d) => ({ ...d, onsetDate: e.target.value || null }))
            }
          />
        </div>
      </div>
      {error && (
        <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}
      <div className="mt-3 flex flex-wrap justify-end gap-2">
        <Button type="button" variant="outline" onClick={onDone}>
          Hủy
        </Button>
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? "Đang lưu…" : "Thêm"}
        </Button>
      </div>
    </form>
  );
}
